// ループスライダー
export const DEFAULT_SLIDE_SPEED = 6700;
const LIST_ITEM_MARGIN = 24;

type SliderOptions = {
  slideSpeed: number;
};

export function slider(options?: Partial<SliderOptions>): () => void {
  const { slideSpeed = DEFAULT_SLIDE_SPEED } = options ?? {};

  const sliderEl = document.querySelector<HTMLElement>('.adopt-slider');
  const sliderWrap = document.querySelector<HTMLElement>(
    '.adopt-slider .adopt-slider-wrap',
  );
  const sliderList = document.querySelector<HTMLElement>('.adopt-slider-list');
  const sliderListItem = document.querySelector<HTMLElement>(
    '.adopt-slider-list > li',
  );

  if (!sliderEl || !sliderWrap || !sliderList || !sliderListItem) {
    return function cleanup() {};
  }

  let currentAnimation: Animation | null = null;
  let savedPosition = 0;

  const listItemCount = sliderList.childElementCount;
  const listItemWidth = sliderListItem.clientWidth;
  const listItemHeight = sliderListItem.clientHeight;
  const listWidth =
    listItemWidth * listItemCount + LIST_ITEM_MARGIN * listItemCount;

  if (listItemCount === 0 || listWidth <= 0) {
    return function cleanup() {};
  }

  sliderEl.style.height = `${listItemHeight}px`;
  sliderList.style.width = `${listWidth}px`;

  /**
   * 必要な数のスライダークローンを動的に作成・調整する
   */
  function createClones(cloneCount: number) {
    const existingClones = sliderWrap!.querySelectorAll(
      '.adopt-slider-list.slider-clone',
    );
    const currentCloneCount = existingClones.length;

    if (currentCloneCount === cloneCount) {
      return;
    }

    if (currentCloneCount > cloneCount) {
      for (let i = cloneCount; i < currentCloneCount; i++) {
        existingClones[i].remove();
      }
    } else {
      const additionalCloneCount = cloneCount - currentCloneCount;
      for (let i = 0; i < additionalCloneCount; i++) {
        const sliderListClone = sliderList!.cloneNode(true) as HTMLElement;
        sliderListClone.classList.add('slider-clone');
        sliderWrap!.appendChild(sliderListClone);
      }
    }
  }

  /**
   * 現在実行中のスライダーアニメーションを停止する
   */
  function stopAnimation() {
    if (currentAnimation && sliderWrap) {
      const computedStyle = window.getComputedStyle(sliderWrap);
      const currentLeft = parseInt(computedStyle.left, 10) || 0;
      savedPosition = currentLeft;
      currentAnimation.onfinish = null;
      currentAnimation.cancel();
      currentAnimation = null;
    }
  }

  /**
   * スライダーの位置アニメーションを実行する
   */
  function sliderPosition() {
    const fromPosition = savedPosition;
    const moveDistance = Math.abs(-listWidth - fromPosition);
    const adjustedDuration = Math.floor(
      (moveDistance / listWidth) * (slideSpeed * listItemCount),
    );

    currentAnimation = sliderWrap!.animate(
      { left: [`${fromPosition}px`, `-${listWidth}px`] },
      { duration: adjustedDuration, easing: 'linear', fill: 'forwards' },
    );

    currentAnimation.onfinish = () => {
      savedPosition = 0;
      sliderWrap!.style.left = '0px';
      sliderPosition();
    };
  }

  /**
   * スライダーを初期化
   */
  function initializeSlider() {
    if (!sliderEl || !sliderWrap || !sliderList) {
      return;
    }

    stopAnimation();

    const requiredCloneCount = Math.ceil(sliderEl.clientWidth / listWidth);

    createClones(requiredCloneCount);

    const totalWidth = listWidth * (requiredCloneCount + 1);
    sliderWrap.style.width = `${totalWidth}px`;

    if (savedPosition === 0) {
      sliderWrap.style.left = '0px';
    } else {
      sliderWrap.style.left = `${savedPosition}px`;
    }

    sliderPosition();
  }

  let debounceTimeoutId: number | null = null;

  function handleResize() {
    if (debounceTimeoutId) {
      clearTimeout(debounceTimeoutId);
    }

    debounceTimeoutId = window.setTimeout(() => {
      initializeSlider();
    }, 300);
  }

  window.addEventListener('resize', handleResize);

  initializeSlider();

  return function cleanup() {
    window.removeEventListener('resize', handleResize);
    if (debounceTimeoutId) {
      clearTimeout(debounceTimeoutId);
    }
    stopAnimation();
  };
}
